import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import cmitLogo from '../assets/cmit-logo.png';

/**
 * The new-client onboarding presentation — four slides shown to a client at
 * kickoff: welcome, your team, what happens during onboarding, how to get
 * support.
 *
 * CONDENSED FROM the eleven-page onboarding guide written for IS Technology,
 * rebranded to CMIT Solutions. The guide's product names (its MDR, backup and
 * agreement names) were that business's stack, so they are described here by
 * what they do rather than by vendor.
 *
 * AN IN-APP VIEW, NOT A PDF. It is set in the app's own tokens and logo, so a
 * brand change reaches it without anyone regenerating a file, and editing the
 * words is a code change reviewed like any other.
 *
 * THE SLIDES ARE PAPER. They use the fixed brand ramps (--brand-*, --accent-*,
 * --white), not the semantic surfaces, so the deck looks the same projected in
 * a meeting whatever theme the presenter's app is in. Only the backdrop and
 * the controls around it follow the theme.
 */

const STAGE_W = 1280;
const STAGE_H = 720;

export function ClientOnboardingDeck({ onClose }: { onClose: () => void }) {
  const [slide, setSlide] = useState(0);
  const total = SLIDES.length;
  const go = useCallback((d: number) => setSlide((s) => Math.min(total - 1, Math.max(0, s + d))), [total]);

  /* Held in a ref so the key handler below is bound once. */
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') go(1);
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') go(-1);
      else if (e.key === 'Home') setSlide(0);
      else if (e.key === 'End') setSlide(total - 1);
      else if (e.key === 'Escape' && !document.fullscreenElement) close.current();
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, [go, total]);

  const toggleFullscreen = () => {
    const root = document.documentElement;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else if (root.requestFullscreen) void root.requestFullscreen().catch(() => undefined);
  };

  const Slide = SLIDES[slide] ?? WelcomeSlide;
  return createPortal(
    <div className="ob-deck" role="dialog" aria-modal="true" aria-label="Client onboarding presentation">
      <Stage>
        <Slide />
      </Stage>
      <div className="ob-deck-bar">
        <button type="button" disabled={slide === 0} onClick={() => go(-1)} aria-label="Previous slide">
          ←
        </button>
        <span>
          {slide + 1} / {total}
        </span>
        <button type="button" disabled={slide === total - 1} onClick={() => go(1)} aria-label="Next slide">
          →
        </button>
        <button type="button" className="ob-deck-bar-text" onClick={toggleFullscreen}>
          Full screen
        </button>
        <button type="button" className="ob-deck-bar-text" onClick={onClose}>
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
}

/** Lays a 1280 × 720 slide out once and scales it to fit the window. */
function Stage({ children }: { children: ReactNode }) {
  const outer = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = outer.current;
    if (!el) return;
    const fit = () => setScale(Math.min(el.clientWidth / STAGE_W, el.clientHeight / STAGE_H));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={outer} className="ob-deck-stage">
      <div className="ob-deck-stage-inner" style={{ width: STAGE_W, height: STAGE_H, transform: `translate(-50%, -50%) scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
}

function Foot({ n }: { n: number }) {
  return (
    <footer className="ob-foot">
      <img src={cmitLogo} alt="CMIT Solutions — Your Technology Team" />
      <span>Client onboarding guide</span>
      <span className="ob-foot-n">{n} / 4</span>
    </footer>
  );
}

function WelcomeSlide() {
  return (
    <section className="ob-slide ob-cover">
      <div className="ob-cover-band">
        <img className="ob-cover-logo" src={cmitLogo} alt="CMIT Solutions — Your Technology Team" />
        <p className="ob-eyebrow">Client onboarding guide</p>
        <h1>Welcome to CMIT&nbsp;Solutions</h1>
        <p className="ob-cover-tag">We are dedicated to helping our clients think differently about how they approach technology. We succeed when you do.</p>
      </div>
      <div className="ob-cover-letter">
        <h2>Thank you for choosing us</h2>
        <p>
          A collaborative partnership is the start of a long-term relationship. We are committed to unparalleled service, innovative
          solutions and a standard of excellence that goes beyond expectations.
        </p>
        <p>
          Onboarding begins as soon as your managed services agreement is signed. Our team will guide you through every step, so you can
          rest assured you are in good hands.
        </p>
        <p>We are passionate about what we do, and all feedback is welcome — reach out to us at any time.</p>
        <p className="ob-sign">Your CMIT Solutions team</p>
      </div>
    </section>
  );
}

function TeamSlide() {
  const roles = [
    {
      title: 'Account Manager',
      body: 'Your strategic partner. Plans your technology roadmap and IT budget with you, and makes sure you know every account management service available to you.',
    },
    {
      title: 'Service Delivery Manager',
      body: 'Leads your transition to managed services and trains your team on how to log a ticket with our helpdesk.',
    },
    {
      title: 'Technical Team',
      body: 'Our helpdesk and engineers. They monitor, maintain and support your systems day to day — remotely, and on site when needed.',
    },
  ];
  return (
    <section className="ob-slide">
      <p className="ob-eyebrow">Support team introduction</p>
      <h2 className="ob-title">Meet your team</h2>
      <p className="ob-lede">
        You will be introduced to your account manager and service delivery manager early on, so you know how to work with us. Our goal is a
        smooth, stress-free transition to managed services.
      </p>
      <div className="ob-roles">
        {roles.map((r, i) => (
          <article key={r.title} className="ob-role">
            <span className="ob-role-n">{i + 1}</span>
            <h3>{r.title}</h3>
            <p>{r.body}</p>
          </article>
        ))}
      </div>
      <p className="ob-ask">
        <strong>We will ask:</strong> When is your budget cycle? Have you set an IT budget before?
      </p>
      <Foot n={2} />
    </section>
  );
}

function ProcessSlide() {
  const steps = [
    {
      when: 'Week 1',
      title: 'Setup & discovery',
      items: [
        'Contacts, locations and current IT bills',
        'Passwords and documentation',
        'Every application your business runs',
        'Cybersecurity review and first safeguards',
      ],
    },
    {
      when: 'Weeks 1–2',
      title: 'Agents & network',
      items: [
        'Our agent on every workstation and server',
        'Network discovery: printers, switches, access points',
        'Photos of network equipment and the data closet',
        'A handover call with your current IT provider',
      ],
    },
    {
      when: 'Quoted project',
      title: 'Standardize',
      items: [
        'Review of hardware against our minimum standards',
        'A detailed quote — e.g. Microsoft 365, replacing outdated machines or servers',
        'Scheduled cutover with an engineer on site',
        'Training for your staff on the new setup',
      ],
    },
    {
      when: 'Ongoing',
      title: 'Managed service',
      items: [
        'Fixed monthly fee per computer user',
        'Managed detection & response security',
        'Microsoft 365 backup and licensing',
        'Helpdesk support and proactive maintenance',
      ],
    },
  ];
  return (
    <section className="ob-slide">
      <p className="ob-eyebrow">What happens during onboarding</p>
      <h2 className="ob-title">From signature to support</h2>
      <ol className="ob-steps">
        {steps.map((s, i) => (
          <li key={s.title} className="ob-step">
            <div className="ob-step-head">
              <span className="ob-step-n">{i + 1}</span>
              <span className="ob-step-when">{s.when}</span>
            </div>
            <h3>{s.title}</h3>
            <ul>
              {s.items.map((it) => (
                <li key={it}>{it}</li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
      <div className="ob-need">
        <strong>What we need from you</strong>
        <span>A staff contact to help install agents</span>
        <span>Admin access to email (Microsoft 365 / Google) and DNS</span>
        <span>Who manages your website</span>
        <span>Network equipment logins</span>
      </div>
      <Foot n={3} />
    </section>
  );
}

function SupportSlide() {
  const ways = [
    { title: 'Log a ticket', body: 'Your service delivery manager will show your team how to reach our helpdesk and log a ticket during onboarding.' },
    { title: 'Remote support', body: 'With our agent installed, our technicians can connect to your machines and resolve most issues without a site visit.' },
    { title: 'Plan ahead', body: 'Talk to your account manager about projects, budget and anything that is not a day-to-day issue.' },
  ];
  return (
    <section className="ob-slide ob-close">
      <div className="ob-close-main">
        <p className="ob-eyebrow">How to get support</p>
        <h2 className="ob-title">We are here when you need us</h2>
        <div className="ob-ways">
          {ways.map((w) => (
            <article key={w.title} className="ob-way">
              <h3>{w.title}</h3>
              <p>{w.body}</p>
            </article>
          ))}
        </div>
      </div>
      <div className="ob-close-band">
        <p className="ob-thanks">
          Thank you!
          <br />
          We are glad you are here.
        </p>
      </div>
      <Foot n={4} />
    </section>
  );
}

const SLIDES = [WelcomeSlide, TeamSlide, ProcessSlide, SupportSlide];
